import React from 'react';
import { useSearchParams } from 'react-router-dom';
import { useAuth } from 'react-oidc-context';
import styles from './Login.module.css';

/**
 * Instructor / admin sign-in via ZewstID. Identical pattern to the web-ide
 * Login — `react-oidc-context` handles the PKCE redirect against the
 * instructor-dashboard OAuth client. Role-gating happens in App.tsx after
 * the callback: staff get the dashboard, while configured exact reviewers
 * get only the read-only grade-appeal surface.
 */
const Login: React.FC = () => {
  const oidc = useAuth();
  const [search] = useSearchParams();
  const errorParam = search.get('error');
  const oidcErrorMessage = oidc.error?.message ?? null;

  const handleZewstIDSignIn = () => {
    oidc.signinRedirect().catch(() => {
      // Errors surface through oidc.error; nothing else to do.
    });
  };

  const handleIdentitySwitch = () => {
    oidc.signoutRedirect().catch(async () => {
      await oidc.removeUser();
      window.location.assign('/login');
    });
  };

  const authenticatedButDenied = oidc.isAuthenticated;

  return (
    <div className={styles.container}>
      <div className={styles.card}>
        <h1 className={styles.title}>
          {authenticatedButDenied ? 'Access denied' : 'Instructor Dashboard'}
        </h1>
        <p className={styles.subtitle}>
          {authenticatedButDenied
            ? 'This identity does not have a staff role under the configured instructor client, or reviewer access is disabled.'
            : 'Sign in with your Zewst account'}
        </p>

        {authenticatedButDenied ? (
          <button
            type="button"
            onClick={handleIdentitySwitch}
            className={styles.submitBtn}
          >
            Sign out and use another account
          </button>
        ) : (
          <button
            type="button"
            onClick={handleZewstIDSignIn}
            disabled={oidc.isLoading}
            className={styles.submitBtn}
          >
            {oidc.isLoading ? 'Connecting…' : 'Sign in with ZewstID'}
          </button>
        )}

        {(errorParam || oidcErrorMessage) && (
          <div className={styles.error} style={{ marginTop: 12 }}>
            {errorParam ? decodeURIComponent(errorParam) : oidcErrorMessage}
          </div>
        )}

        <p className={styles.note}>
          Instructor and administrator accounts get the dashboard. Authorized
          reviewers get read-only appeal access.
        </p>
      </div>
    </div>
  );
};

export default Login;
